(function () {
  if (window.UqoraAutoDiscount) return;

  const URL_KEYS = {
    code: ["autoApplyDiscountCode", "discount_code", "discount"],
    percent: ["autoApplyDiscountPercent", "discount_percent"],
    enabled: ["autoApplyDiscountEnabled", "discount_experience"],
    alreadyApplied: ["autoApplyDiscountApplied", "discount_applied"],
  };

  const EVENTS = {
    vwoUpdate: "uqora:vwo-auto-discount",
    stateUpdated: "uqora:auto-discount-updated",
  };

  const DEFAULT_PERCENT = 15;
  // Persistent marker that the discount code is already on the user's Shopify
  // cart. Lives in localStorage (not sessionStorage) so it survives across
  // tabs and sessions on the same device — that lets us skip the redundant
  // /cart/update.js POST that would otherwise trigger Rebuy's Smart Cart auto-
  // open behavior on revisits. Stored as `<code>|<timestamp>`; we expire after
  // 7 days, which is shorter than Shopify's ~14-day cart cookie, so we re-
  // verify before the cart is at risk of having expired underneath us.
  const APPLIED_CODE_LOCAL_KEY = "uqoraAutoDiscountAppliedCode";
  const APPLIED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  // In-memory config — resets to disabled on every fresh page load.
  // VWO (or any caller) must call setConfig() on each page to activate the discount.
  let _config = {
    enabled: false,
    code: "",
    percentage: 0,
  };

  const readStorage = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (storage, key, value) => {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Applied-code marker helpers. Round-trips `<normalizedCode>|<timestamp>` to
  // localStorage; returns null when the marker is missing, malformed, or older
  // than APPLIED_TTL_MS. Callers should treat null as "unknown — POST to be
  // safe" and a matching string as "already applied — skip POST".
  const readAppliedCode = () => {
    const raw = readStorage(window.localStorage, APPLIED_CODE_LOCAL_KEY);
    if (!raw) return null;
    const sep = raw.indexOf("|");
    if (sep < 0) return null;
    const code = raw.slice(0, sep);
    const ts = Number(raw.slice(sep + 1));
    if (!code || !Number.isFinite(ts)) return null;
    if (Date.now() - ts > APPLIED_TTL_MS) return null;
    return code;
  };

  const writeAppliedCode = (normalizedCode) => {
    writeStorage(
      window.localStorage,
      APPLIED_CODE_LOCAL_KEY,
      `${normalizedCode}|${Date.now()}`
    );
  };

  const clearAppliedCode = () => {
    try {
      window.localStorage.removeItem(APPLIED_CODE_LOCAL_KEY);
    } catch (_) {}
  };

  const roundCurrency = (value) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return 0;
    return Math.round((numericValue + Number.EPSILON) * 100) / 100;
  };

  const parsePercentValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed > 100) return null;
    return parsed;
  };

  const parsePercentFromCode = (code) => {
    if (!code) return null;
    const match = code.match(/(\d+(?:\.\d+)?)/);
    if (!match) return null;
    return parsePercentValue(match[1]);
  };

  const parseBooleanValue = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = `${value}`.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    return null;
  };

  const getFirstQueryParamValue = (queryParams, keys) => {
    for (const key of keys) {
      const value = queryParams.get(key);
      if (value !== null && value !== "") return value;
    }
    return null;
  };

  const getConfig = () => ({ ..._config });

  // Last detail emitted via `stateUpdated`. Late-binding listeners (e.g. a
  // buy-box custom element that upgrades AFTER VWO has already fired its
  // event) can read this on register to recover state they missed.
  let _lastEmittedConfig = null;
  const getLastEmittedConfig = () => (_lastEmittedConfig ? { ..._lastEmittedConfig } : null);

  const emitStateUpdate = () => {
    const detail = getConfig();
    _lastEmittedConfig = detail;
    window.dispatchEvent(
      new CustomEvent(EVENTS.stateUpdated, { detail })
    );
  };

  const applyDiscountToPrice = (price, config = getConfig()) => {
    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return 0;

    if (!config.enabled || config.percentage <= 0) {
      return roundCurrency(numericPrice);
    }

    // Mirror Shopify's server-side calculation: floor the discount amount in cents,
    // then subtract — avoids the 1-cent rounding mismatch vs. the cart price.
    const priceInCents = Math.round(numericPrice * 100);
    const discountInCents = Math.floor(priceInCents * config.percentage / 100);
    return (priceInCents - discountInCents) / 100;
  };

  const getCartUpdateUrl = () => {
    const basePath =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return `${basePath}cart/update.js`;
  };

  // In-flight cart-update fetch. Serializes concurrent apply/clear so
  // multiple callers (IIFE init, setConfig side-effect, per-buybox connect,
  // and state-update handler) don't race the Shopify cart API. Callers can
  // await `pendingCartUpdate` before issuing follow-on actions like add-to-cart.
  let _pendingCartUpdate = null;

  const postDiscount = (discount, { sections, sectionsUrl } = {}) => {
    if (_pendingCartUpdate) return _pendingCartUpdate;
    const body = { discount };
    if (Array.isArray(sections) && sections.length) {
      body.sections = sections;
      body.sections_url = sectionsUrl || window.location.pathname;
    }
    _pendingCartUpdate = fetch(getCartUpdateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).finally(() => {
      _pendingCartUpdate = null;
    });
    return _pendingCartUpdate;
  };

  const clearDiscountCode = async () => {
    if (!readAppliedCode()) return;

    try {
      const response = await postDiscount("");
      if (response.ok) {
        clearAppliedCode();
      }
    } catch (error) {
      console.warn("Failed to clear discount code", error);
    }
  };

  // In-memory dedupe: skip retrying the same code repeatedly within a short
  // window. Bridges the gap between sequential applyDiscountCode calls (from
  // the IIFE, buybox connectedCallback, and autoDiscountUpdatedHandler) when
  // the localStorage marker hasn't been written yet — e.g. when Shopify
  // reports the code as attached-but-not-yet-applicable on an empty cart and
  // our first POST resolves before the next caller checks the marker.
  let _lastAttemptedCode = null;
  let _lastAttemptedAt = 0;
  const ATTEMPT_DEDUPE_MS = 5000;

  const applyDiscountCode = async (
    code,
    { force = false, sections = [], sectionsUrl } = {}
  ) => {
    const safeCode = (code || "").trim();
    if (!safeCode) return;

    const normalizedCode = safeCode.toLowerCase();
    if (!force && readAppliedCode() === normalizedCode) return null;

    if (
      !force &&
      _lastAttemptedCode === normalizedCode &&
      Date.now() - _lastAttemptedAt < ATTEMPT_DEDUPE_MS
    ) {
      return null;
    }
    _lastAttemptedCode = normalizedCode;
    _lastAttemptedAt = Date.now();

    try {
      const response = await postDiscount(safeCode, { sections, sectionsUrl });
      if (!response.ok) {
        console.warn("uqora auto-discount: cart update failed", response.status);
        return null;
      }
      // Shopify can return 200 with the discount rejected — check `applicable`
      // before caching success, otherwise we silently never retry. Exception:
      // on an empty cart, Shopify reports applicable: false (nothing to
      // discount yet) but the code IS attached and will activate once items
      // are added. Treat that case as a successful attach so we cache the
      // marker and avoid re-POSTing on every page load.
      const body = await response.clone().json().catch(() => null);
      const codes = body?.discount_codes || [];
      const matchEntry = codes.find(
        (d) => (d.code || "").toLowerCase() === normalizedCode
      );
      const isAttached = !!matchEntry;
      const isApplicable = isAttached && matchEntry.applicable;
      const isEmptyCart = (body?.item_count || 0) === 0;

      if (isAttached && (isApplicable || isEmptyCart)) {
        writeAppliedCode(normalizedCode);
      } else if (codes.length) {
        console.warn(
          "uqora auto-discount: code reported as not applicable",
          codes
        );
      }
      return body;
    } catch (error) {
      console.warn("Failed to auto-apply discount code", error);
      return null;
    }
  };

  // Final post-add verification. A cached marker only proves that Shopify
  // accepted the code at some earlier point; it does not prove that the code
  // still applies to the active cart. Re-read Shopify's cart-level response
  // after the add and fail the promotional UI closed when it is not applicable.
  const disableUnconfirmedDiscount = () => {
    clearAppliedCode();
    if (_config.enabled) {
      _config.enabled = false;
      emitStateUpdate();
    }
  };

  const ensureDiscountCodeApplied = async (
    code,
    { sections = [], sectionsUrl } = {}
  ) => {
    const safeCode = (code || "").trim();
    if (!safeCode) {
      disableUnconfirmedDiscount();
      return { ok: false, cart: null };
    }

    const cart = await applyDiscountCode(safeCode, {
      force: true,
      sections,
      sectionsUrl,
    });
    const normalizedCode = safeCode.toLowerCase();
    const matchingCode = cart?.discount_codes?.find(
      (entry) => (entry.code || "").toLowerCase() === normalizedCode
    );

    if (matchingCode?.applicable === true) {
      writeAppliedCode(normalizedCode);
      return { ok: true, cart };
    }

    disableUnconfirmedDiscount();
    return { ok: false, cart };
  };

  const setConfig = ({ enabled, code, percentage, applyDiscount = true } = {}) => {
    if (typeof enabled === "boolean") {
      _config.enabled = enabled;
    }

    if (typeof code === "string") {
      _config.code = code.trim();
    }

    if (percentage === null || percentage === "") {
      _config.percentage = parsePercentFromCode(_config.code) || DEFAULT_PERCENT;
    } else if (percentage !== undefined) {
      const safePercentage = parsePercentValue(percentage);
      if (safePercentage) {
        _config.percentage = safePercentage;
      }
    }

    // If enabled with a code but still no valid percentage, derive it from the code
    if (_config.enabled && _config.code && !_config.percentage) {
      _config.percentage = parsePercentFromCode(_config.code) || DEFAULT_PERCENT;
    }

    const config = getConfig();

    if (applyDiscount && config.enabled) {
      applyDiscountCode(config.code);
    } else if (!applyDiscount && config.enabled && config.code) {
      // Caller indicated the code is already on the cart (e.g., URL had
      // ?discount_applied=true). Record it so future apply attempts in this
      // session and on subsequent visits within the TTL window short-circuit.
      writeAppliedCode(config.code.toLowerCase());
    } else if (!config.enabled) {
      clearDiscountCode();
    }

    emitStateUpdate();

    return config;
  };

  const initFromQueryParams = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryCode = getFirstQueryParamValue(queryParams, URL_KEYS.code);
    const queryPercentage = getFirstQueryParamValue(queryParams, URL_KEYS.percent);
    const queryEnabledRaw = getFirstQueryParamValue(queryParams, URL_KEYS.enabled);
    const queryAlreadyAppliedRaw = getFirstQueryParamValue(
      queryParams,
      URL_KEYS.alreadyApplied
    );

    const queryEnabled = parseBooleanValue(queryEnabledRaw);
    const queryAlreadyApplied = parseBooleanValue(queryAlreadyAppliedRaw);
    const hasQueryConfig =
      queryCode !== null ||
      queryPercentage !== null ||
      queryEnabled !== null ||
      queryAlreadyApplied !== null;

    if (!hasQueryConfig) return;

    const currentConfig = getConfig();
    const nextCode = queryCode || currentConfig.code;
    const nextEnabled =
      typeof queryEnabled === "boolean"
        ? queryEnabled
        : Boolean(nextCode || currentConfig.enabled);

    setConfig({
      enabled: nextEnabled,
      code: nextCode,
      percentage: queryPercentage,
      applyDiscount: queryAlreadyApplied !== true,
    });
  };

  window.addEventListener(EVENTS.vwoUpdate, (event) => {
    // A qualified campaign link owns the discount state for its campaign
    // window. Do not let a later VWO payload replace LABORDAY with the
    // standard paid-media offer on the same visit.
    if (window.UqoraCampaignOffers?.getActiveOffer?.()) return;

    const payload = event.detail || {};
    setConfig({
      enabled: payload.enabled,
      code: payload.code,
      percentage: payload.percentage,
      applyDiscount: payload.applyDiscount !== false,
    });
  });

  window.UqoraAutoDiscount = {
    events: EVENTS,
    getConfig,
    getLastEmittedConfig,
    setConfig,
    applyDiscountToPrice,
    applyDiscountCode,
    ensureDiscountCodeApplied,
    clearDiscountCode,
    get pendingCartUpdate() {
      return _pendingCartUpdate;
    },
  };

  initFromQueryParams();

  // Pick up any VWO config that fired before this deferred script loaded.
  // VWO runs synchronously in <head> before defer scripts execute, so the
  // uqora:vwo-auto-discount event is missed. VWO variant code must store its
  // payload in window._uqoraVWOPendingConfig before dispatching the event.
  if (window._uqoraVWOPendingConfig) {
    const pending = window._uqoraVWOPendingConfig;
    delete window._uqoraVWOPendingConfig;
    setConfig({
      enabled: pending.enabled,
      code: pending.code,
      percentage: pending.percentage,
      applyDiscount: pending.applyDiscount !== false,
    });
  }

  // Intentionally no unconditional apply/clear in the IIFE tail. All state transitions
  // run through setConfig (from initFromQueryParams, the pending stash above,
  // and the uqora:vwo-auto-discount event listener), which calls
  // applyDiscountCode or clearDiscountCode explicitly. An unconditional clear
  // would race with VWO's variant code: if VWO fires AFTER this IIFE on a
  // given page load, _config.enabled is still false here and a clear would
  // POST `discount: ""`, then the late VWO event would POST the apply right
  // after — two cart-state mutations in quick succession, which Rebuy
  // interprets as a cart change worth opening Smart Cart for.
})();
